import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  FileText, 
  Download, 
  Copy, 
  Clock, 
  Tag,
  Shield,
  Globe,
  AlertTriangle,
  Eye,
  Code,
  ChevronRight,
  ChevronDown
} from 'lucide-react'
import { api } from '../lib/api'

interface ObjectViewerPageProps {
  objectId?: string
  collectionId?: string
  onClose?: () => void
}

export default function ObjectViewerPage({ objectId, collectionId, onClose }: ObjectViewerPageProps) {
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'properties']))

  // For demo purposes, create a sample STIX object when no objectId is provided
  const sampleObject = {
    id: 'indicator--c410e480-e42b-47d1-9476-85307c12bcbp',
    type: 'indicator',
    spec_version: '2.1',
    created: '2024-01-15T10:30:00.000Z',
    modified: '2024-01-15T10:30:00.000Z',
    pattern: "[file:hashes.MD5 = 'd41d8cd98f00b204e9800998ecf8427e']",
    labels: ['malicious-activity', 'backdoor'],
    name: 'Suspicious File Hash',
    description: 'MD5 hash identified as malicious by threat intelligence feeds',
    confidence: 85,
    valid_from: '2024-01-15T00:00:00.000Z',
    kill_chain_phases: [
      {
        kill_chain_name: 'mitre-attack',
        phase_name: 'initial-access'
      }
    ],
    external_references: [
      {
        source_name: 'mitre-attack',
        url: 'https://attack.mitre.org/techniques/T1566/',
        description: 'MITRE ATT&CK Technique T1566'
      }
    ]
  }

  const objectData = objectId ? null : sampleObject // In real implementation, fetch by objectId

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'indicator':
        return <AlertTriangle className="h-5 w-5" />
      case 'malware':
        return <Shield className="h-5 w-5" />
      case 'vulnerability':
        return <Eye className="h-5 w-5" />
      default:
        return <FileText className="h-5 w-5" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'indicator':
        return 'bg-red-100 text-red-800'
      case 'malware':
        return 'bg-orange-100 text-orange-800'
      case 'vulnerability':
        return 'bg-yellow-100 text-yellow-800'
      case 'campaign':
        return 'bg-purple-100 text-purple-800'
      case 'threat-actor':
        return 'bg-pink-100 text-pink-800'
      default:
        return 'bg-blue-100 text-blue-800'
    }
  }

  const formatStixType = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const renderProperty = (key: string, value: any, depth = 0) => {
    const indent = depth * 20

    if (value === null || value === undefined) {
      return (
        <div className="flex items-center space-x-2" style={{ marginLeft: indent }}>
          <span className="text-sm font-medium text-gray-600">{key}:</span>
          <span className="text-sm text-gray-400">null</span>
        </div>
      )
    }

    if (typeof value === 'boolean') {
      return (
        <div className="flex items-center space-x-2" style={{ marginLeft: indent }}>
          <span className="text-sm font-medium text-gray-600">{key}:</span>
          <span className={`text-sm ${value ? 'text-green-600' : 'text-red-600'}`}>
            {value.toString()}
          </span>
        </div>
      )
    }

    if (typeof value === 'number') {
      return (
        <div className="flex items-center space-x-2" style={{ marginLeft: indent }}>
          <span className="text-sm font-medium text-gray-600">{key}:</span>
          <span className="text-sm text-gray-900">{value}</span>
        </div>
      )
    }

    if (typeof value === 'string') {
      if (value.startsWith('http')) {
        return (
          <div className="flex items-center space-x-2" style={{ marginLeft: indent }}>
            <span className="text-sm font-medium text-gray-600">{key}:</span>
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
              {value}
            </a>
          </div>
        )
      }
      return (
        <div className="flex items-center space-x-2" style={{ marginLeft: indent }}>
          <span className="text-sm font-medium text-gray-600">{key}:</span>
          <span className="text-sm text-gray-900 break-all">{value}</span>
        </div>
      )
    }

    if (Array.isArray(value)) {
      const isExpanded = expandedSections.has(`${key}-${depth}`)
      return (
        <div style={{ marginLeft: indent }}>
          <button
            onClick={() => toggleSection(`${key}-${depth}`)}
            className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>{key} ({value.length} items):</span>
          </button>
          {isExpanded && (
            <div className="mt-2 space-y-2">
              {value.map((item, index) => (
                <div key={index}>
                  {typeof item === 'object' ? 
                    Object.entries(item).map(([k, v]) => renderProperty(k, v, depth + 1)) :
                    <div className="text-sm text-gray-900" style={{ marginLeft: (depth + 1) * 20 }}>
                      {item}
                    </div>
                  }
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    if (typeof value === 'object') {
      const isExpanded = expandedSections.has(`${key}-${depth}`)
      return (
        <div style={{ marginLeft: indent }}>
          <button
            onClick={() => toggleSection(`${key}-${depth}`)}
            className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>{key}:</span>
          </button>
          {isExpanded && (
            <div className="mt-2 space-y-2">
              {Object.entries(value).map(([k, v]) => renderProperty(k, v, depth + 1))}
            </div>
          )}
        </div>
      )
    }

    return null
  }

  if (!objectData) {
    return (
      <div className="p-6">
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Object Not Found</h3>
          <p className="text-gray-500">
            The requested STIX object could not be found or you don't have permission to view it.
          </p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-4 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              Back to Search
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {onClose && (
        <div className="mb-4">
          <button
            onClick={onClose}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            ← Back to Search
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex items-start space-x-4">
              <div className={`p-2 rounded-md ${getTypeColor(objectData.type)}`}>
                {getTypeIcon(objectData.type)}
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <h1 className="text-xl font-bold text-gray-900">
                    {objectData.name || formatStixType(objectData.type)}
                  </h1>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(objectData.type)}`}>
                    {formatStixType(objectData.type)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-2">{objectData.id}</p>
                {objectData.description && (
                  <p className="text-gray-700 max-w-2xl">{objectData.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex bg-gray-100 rounded-md p-1">
                <button
                  onClick={() => setViewMode('formatted')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'formatted' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Formatted
                </button>
                <button
                  onClick={() => setViewMode('raw')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'raw' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Raw JSON
                </button>
              </div>
              <button
                onClick={() => copyToClipboard(JSON.stringify(objectData, null, 2))}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                title="Copy to clipboard"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                title="Download object"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {viewMode === 'formatted' ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <button
                  onClick={() => toggleSection('basic')}
                  className="flex items-center space-x-2 text-lg font-medium text-gray-900 hover:text-gray-700 mb-4"
                >
                  {expandedSections.has('basic') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  <span>Basic Information</span>
                </button>
                {expandedSections.has('basic') && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">Created:</span>
                        <span className="text-sm text-gray-900">{formatDate(objectData.created)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">Modified:</span>
                        <span className="text-sm text-gray-900">{formatDate(objectData.modified)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Tag className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">Spec Version:</span>
                        <span className="text-sm text-gray-900">{objectData.spec_version}</span>
                      </div>
                      {objectData.confidence && (
                        <div className="flex items-center space-x-2">
                          <Shield className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-600">Confidence:</span>
                          <span className="text-sm text-gray-900">{objectData.confidence}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Labels */}
              {objectData.labels && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Labels</h3>
                  <div className="flex flex-wrap gap-2">
                    {objectData.labels.map((label: string, index: number) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pattern (for indicators) */}
              {objectData.pattern && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Pattern</h3>
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    {objectData.pattern}
                  </div>
                </div>
              )}

              {/* Kill Chain Phases */}
              {objectData.kill_chain_phases && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Kill Chain Phases</h3>
                  <div className="space-y-2">
                    {objectData.kill_chain_phases.map((phase: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-900">{phase.kill_chain_name}</div>
                        <div className="text-sm text-gray-600">{phase.phase_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* External References */}
              {objectData.external_references && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">External References</h3>
                  <div className="space-y-3">
                    {objectData.external_references.map((ref: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <Globe className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">{ref.source_name}</span>
                        </div>
                        {ref.url && (
                          <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">
                            {ref.url}
                          </a>
                        )}
                        {ref.description && (
                          <p className="text-sm text-gray-600 mt-1">{ref.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Properties */}
              <div>
                <button
                  onClick={() => toggleSection('properties')}
                  className="flex items-center space-x-2 text-lg font-medium text-gray-900 hover:text-gray-700 mb-4"
                >
                  {expandedSections.has('properties') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  <span>All Properties</span>
                </button>
                {expandedSections.has('properties') && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {Object.entries(objectData).map(([key, value]) => renderProperty(key, value))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Code className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Raw JSON</span>
              </div>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-auto">
                {JSON.stringify(objectData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}